import { useRef, useState, useEffect } from 'react';
import { OPENAI_REALTIME_URL, OPENAI_REALTIME_MODEL, DEFAULT_HEADERS } from '../../../config';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Message } from '../types';

export const useWebRTC = (
  onError: (error: string) => void,
  aiVoice: string,
  conversationTopic: string,
  addMessage: (msg: Message) => void,
  onWorkspaceRequest?: (type: string, topic: string, context: string, word?: string, definition?: string, examples?: string[], assessmentData?: any) => Promise<{ success: boolean; message: string; content?: string } | void>,
  userName?: string | null,
  onSessionMetadata?: (title: string, description: string, category: string) => void,
  onDebugUpdate?: (info: { session?: any; meta?: any; events?: any[] }) => void
) => {
  const conversationTopicRef = useRef(conversationTopic);
  conversationTopicRef.current = conversationTopic;
  
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const micSenderRef = useRef<RTCRtpSender | null>(null);
  const micTrackRef = useRef<MediaStreamTrack | null>(null);
  const pendingUserRef = useRef<Set<string>>(new Set());
  const aiBufferRef = useRef<Record<string, string>>({});
  const functionCallBuffer = useRef<string>('');
  const initialResponseSentRef = useRef<boolean>(false);
  const hasTextStreamByItemRef = useRef<Record<string, boolean>>({});
  const sessionOverridesRef = useRef<{ instructions?: string; voice?: string } | null>(null);
  const simulationModeRef = useRef<boolean>(false);
  const simConfigRef = useRef<{ instructions: string; voice?: string } | null>(null);
  const lastSentSessionRef = useRef<any>(null);
  const debugEnabledRef = useRef<boolean>(false);
  const metaRef = useRef<any | null>(null);
  const debugEventsRef = useRef<any[]>([]);
  const assistantMsgInitializedRef = useRef<Set<string>>(new Set());
  const assistantFinalizedRef = useRef<Set<string>>(new Set());
  const userFinalizedRef = useRef<Set<string>>(new Set());
  const lastAssistantDeltaByItemRef = useRef<Record<string, string>>({});
  const responseIdToItemIdRef = useRef<Record<string, string>>({});
  const extraHeadersRef = useRef<Record<string, string> | null>(null);
  // Track tool call lifecycle for debug grouping/dedupe
  const callStartByIdRef = useRef<Record<string, number>>({});
  const seenFunctionCallsRef = useRef<Set<string>>(new Set());
  // Learning path tool guardrails (prevent repeated failing calls)
  const learningPathAttemptedRef = useRef<boolean>(false);
  const learningPathCooldownUntilRef = useRef<number>(0);
  // Allow per-session realtime model override
  const overrideModelRef = useRef<string | null>(null);

  const debugPush = (event: string, data?: any) => {
    try {
      if (!debugEnabledRef.current) return;
      const w: any = window as any;
      w._tallyDebug = w._tallyDebug || { logs: [] as any[] };
      const entry = { ts: new Date().toISOString(), event, data };
      w._tallyDebug.logs.push(entry);
      if (w._tallyDebug.logs.length > 500) w._tallyDebug.logs.shift();
      // Mirror to console (removed TALLY-DEBUG tag)
      console.log(event, data);
    } catch {}
  };

  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [disconnectCountdown, setDisconnectCountdown] = useState<number | null>(null);
  const conversationTurnRef = useRef(0);
  const metadataGeneratedRef = useRef(false);
  const serverSessionRef = useRef<any | null>(null);
  const lastUserFinalRef = useRef<{ itemId: string; text: string } | null>(null);
  const awaitingAssistantForRef = useRef<string | null>(null);
  const remoteMutedRef = useRef(false);
  

  interface ResumeData {
    transcript: Message[];
    sessionId: string;
    lastMessageTime: string | number;
    sessionMetadata?: any;
    topic?: string;
  }

  const startConversation = async (overrideTopic?: string, resumeData?: ResumeData) => {
    const topicToUse = overrideTopic || resumeData?.topic || conversationTopicRef.current;
    console.log('🎮 Starting WebRTC conversation with topic:', topicToUse);
    if (resumeData) {
      console.log('📂 Resuming session:', resumeData.sessionId);
    }
    conversationTopicRef.current = topicToUse; // Update ref with override if provided
    setConnecting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      const audioTrack = stream.getAudioTracks()[0];
      micTrackRef.current = audioTrack;

      const baseUrl = process.env.NODE_ENV === 'development'
        ? '/Prod/secure/realtime-session-v2'
        : `${process.env.REACT_APP_API_BASE_URL}/secure/realtime-session-v2`;
      const apiRoot = process.env.NODE_ENV === 'development'
        ? '/Prod'
        : (process.env.REACT_APP_API_BASE_URL || '');
      const executeUrl = `${apiRoot.replace(/\/$/, '')}/secure/tally-tools/execute`;

      // Fetch Cognito ID token for Authorization
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) throw new Error('Missing ID token');

      const url = `${baseUrl}?userName=${encodeURIComponent(userName || '')}&topic=${encodeURIComponent(topicToUse || '')}`;
      const headerBase: Record<string, string> = { Authorization: `Bearer ${idToken}` };
      const mergedHeaders = extraHeadersRef.current ? { ...headerBase, ...extraHeadersRef.current } : headerBase;
      const sessionResp = await fetch(url, { method: 'GET', headers: mergedHeaders });
      if (!sessionResp.ok) throw new Error(`Session creation failed: ${sessionResp.status}`);
      const { token, session: serverSession, meta } = await sessionResp.json();
      metaRef.current = meta || null;
      if (!serverSession) throw new Error('Server did not return session config');
      serverSessionRef.current = serverSession;
      // Clear one-time extra headers after use
      extraHeadersRef.current = null;

      const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      pcRef.current = pc;

      pc.onconnectionstatechange = () => setConnected(pc.connectionState === 'connected');
      pc.ontrack = ev => { remoteStreamRef.current = ev.streams[0]; };
      const sender = pc.addTrack(audioTrack, stream);
      micSenderRef.current = sender;

      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;

      dc.onopen = () => {
        console.log('Data channel opened');
        debugPush('data_channel.open');
        // Don't send session update here - wait for session.created event
      };

      dc.onmessage = async (ev) => {
        const msg = JSON.parse(ev.data);
        
        // Log only important function-related messages
        if (msg.type === 'session.created' || msg.type === 'error') {
          console.log('🔧 Important event:', msg.type);
        }
        
        // Handle error events
        if (msg.type === 'error') {
          console.error('Realtime API error:', msg);
          console.error('Error details:', msg.error);
          if (msg.error?.message) {
            onError(`API Error: ${msg.error.message}`);
          }
        }
        
        // Handle session.created event
        if (msg.type === 'session.created') {
          console.log('🎯 Session created, sending server-provided configuration...');
          if (!serverSessionRef.current) {
            onError('Missing server session configuration');
            return;
          }
          let outboundSession: any;
          if (simulationModeRef.current) {
            // Build a SIMULATION session config exposing only the scorecard tool
            const simVoice = sessionOverridesRef.current?.voice || 'coral';
            const baseInstructions = sessionOverridesRef.current?.instructions || 'You are a simulated customer.';
            const toolGuidance = `\n\nWhen the conversation is wrapping up (problem fixed, agreed path, the customer has grown increasingly frustrated or the agent (the user) says that nothing else can be done to help), call generate_simulation_scorecard exactly once with: { simulation_type: 'customer_support', resolution_status: 'resolved'|'on_path'|'unresolved', customer_satisfaction_score: 1-10, agent_behavior_scores: { calm_under_pressure: 1-10, deescalating_language: 1-10, empathy_and_understanding: 1-10 }, sliders_snapshot reflecting settings, agent_summary (2-4 sentences), key_moments (optional), and three short lists: highlights[], improvements[], next_steps[] (2-4 concise bullets each). Do not call earlier.`;
            const simInstructions = `${baseInstructions}${toolGuidance}`;
            outboundSession = {
              model: 'gpt-realtime',
              modalities: ['text', 'audio'],
              voice: simVoice,
              input_audio_format: 'pcm16',
              output_audio_format: 'pcm16',
              input_audio_transcription: { model: 'gpt-4o-transcribe', language: 'en' },
              turn_detection: { type: 'server_vad', threshold: 0.5, prefix_padding_ms: 300, silence_duration_ms: 500 },
              tool_choice: 'auto',
              tools: [
                {
                  type: 'function',
                  name: 'generate_simulation_scorecard',
                  description: 'Generate a scorecard at wrap-up for the simulation.',
                  parameters: {
                    type: 'object',
                    properties: {
                      simulation_type: { type: 'string' },
                      resolution_status: { type: 'string', enum: ['resolved', 'on_path', 'unresolved'] },
                      customer_satisfaction_score: { type: 'integer', minimum: 1, maximum: 10 },
                      agent_behavior_scores: {
                        type: 'object',
                        properties: {
                          calm_under_pressure: { type: 'integer', minimum: 1, maximum: 10 },
                          deescalating_language: { type: 'integer', minimum: 1, maximum: 10 },
                          empathy_and_understanding: { type: 'integer', minimum: 1, maximum: 10 }
                        },
                        required: ['calm_under_pressure','deescalating_language','empathy_and_understanding']
                      },
                      sliders_snapshot: {
                        type: 'object',
                        properties: {
                          problem_severity: { type: 'integer', minimum: 1, maximum: 10 },
                          emotional_intensity: { type: 'integer', minimum: 1, maximum: 10 },
                          customer_cooperativeness: { type: 'integer', minimum: 1, maximum: 10 }
                        }
                      },
                      agent_summary: { type: 'string' },
                      key_moments: {
                        type: 'array',
                        items: {
                          type: 'object',
                          properties: {
                            turn_ids: { type: 'array', items: { type: 'string' } },
                            note: { type: 'string' }
                          }
                        }
                      },
                      highlights: { type: 'array', items: { type: 'string' } },
                      improvements: { type: 'array', items: { type: 'string' } },
                      next_steps: { type: 'array', items: { type: 'string' } },
                      notes_for_rubric: { type: 'string' }
                    },
                    required: ['simulation_type','resolution_status','customer_satisfaction_score','agent_behavior_scores']
                  }
                }
              ],
              instructions: simInstructions
            };
            console.log('🧪 SIMULATION mode enabled. Sending simulation-only session configuration.');
            debugPush('session.created.simulation', {
              voice: outboundSession.voice,
              tool_choice: outboundSession.tool_choice,
              toolsCount: outboundSession.tools.length,
              instructionsPreview: String(outboundSession.instructions || '').slice(0, 200)
            });
          } else {
            // Merge minimal overrides (if any) into server-provided session
            const merged = { ...(serverSessionRef.current || {}) } as any;
            if (sessionOverridesRef.current?.instructions) merged.instructions = sessionOverridesRef.current.instructions;
            if (sessionOverridesRef.current?.voice) merged.voice = sessionOverridesRef.current.voice;
            outboundSession = merged;
            debugPush('session.created.regular', {
              voice: outboundSession.voice,
              tool_choice: outboundSession.tool_choice,
              toolsCount: Array.isArray(outboundSession.tools) ? outboundSession.tools.length : 0,
              instructionsPreview: String(outboundSession.instructions || '').slice(0, 200)
            });
          }
          // Clear overrides after applying
          sessionOverridesRef.current = null;
          const sessionUpdate: any = { type: 'session.update', session: outboundSession };
          dc.send(JSON.stringify(sessionUpdate));
          lastSentSessionRef.current = outboundSession;
          // Push initial debug snapshot
          try {
            if (onDebugUpdate) {
              onDebugUpdate({ session: lastSentSessionRef.current, meta: metaRef.current, events: debugEventsRef.current });
            }
          } catch {}
          console.log('✅ Sent session.update', {
            simulationMode: simulationModeRef.current,
            voice: outboundSession?.voice,
            toolsCount: Array.isArray(outboundSession?.tools) ? outboundSession.tools.length : 0,
            toolChoice: outboundSession?.tool_choice,
            instructionsPreview: String(outboundSession?.instructions || '').slice(0, 160)
          });
          debugPush('session.update.sent', {
            simulationMode: simulationModeRef.current,
            voice: outboundSession?.voice,
            toolsCount: Array.isArray(outboundSession?.tools) ? outboundSession.tools.length : 0,
            toolChoice: outboundSession?.tool_choice,
            modalities: outboundSession?.modalities,
            turn_detection: outboundSession?.turn_detection
          });
          console.log('✅ Server tools available (unapplied in sim mode):', (serverSessionRef.current.tools || []).map((t: any) => t.name));

          // Trigger Tally to start the conversation
          console.log('🎙️ Triggering Tally to start conversation...');
          if (!initialResponseSentRef.current) {
            initialResponseSentRef.current = true;
            setTimeout(() => {
              dc.send(JSON.stringify({ type: 'response.create' }));
            }, 500);
          }
        }
        
        // Handle session.updated event to confirm our settings
        if (msg.type === 'session.updated') {
          console.log('ℹ️ session.updated acknowledgement received');
          debugPush('session.updated.ack');
        }
      
        if (msg.type === 'input_audio_buffer.committed') {
          // User audio committed
          pendingUserRef.current.add(msg.item_id);
          console.log('🎤 input committed', { item_id: msg.item_id, pendingCount: pendingUserRef.current.size });
          debugPush('input_audio_buffer.committed', { item_id: msg.item_id, pendingCount: pendingUserRef.current.size });
          // If Tally is speaking, stop her to allow barge-in
          if (dcRef.current && dcRef.current.readyState === 'open' && isAISpeaking) {
            debugPush('response.cancel.barge_in', { activeItems: Object.keys(aiBufferRef.current) });
            dcRef.current.send(JSON.stringify({ type: 'response.cancel' }));
          }
          // Add a placeholder for the user message immediately
          addMessage({
            role: 'user',
            text: '...',
            timestamp: Date.now(),
            itemId: msg.item_id,
            isFinal: false,
          });
        }
      
        if (msg.type === 'input_audio_buffer.speech_started') { setIsSpeaking(true); console.log('🎤 speech_started'); debugPush('speech_started'); }
        if (msg.type === 'input_audio_buffer.speech_stopped') { setIsSpeaking(false); console.log('🎤 speech_stopped'); debugPush('speech_stopped'); }
      
        // Handle user transcription events (like V1 does)
        if (msg.type.startsWith('conversation.item.input_audio_transcription')) {
          const isCompleted = msg.type.endsWith('completed');
          if (!isCompleted) {
            // Ignore intermediate deltas in UI; only render final server ASR
            return;
          }
          const finalText = msg.transcript || '';
          // Flush any buffered assistant fragments that were held during the user's speech
          try {
            const bufferedItems = Object.keys(aiBufferRef.current || {});
            for (const itemId of bufferedItems) {
              const buf = aiBufferRef.current[itemId];
              if (buf && buf.length > 0) {
                if (!assistantMsgInitializedRef.current.has(itemId)) {
                  addMessage({ role: 'assistant', text: '', timestamp: Date.now(), itemId });
                  assistantMsgInitializedRef.current.add(itemId);
                }
                addMessage({ role: 'assistant', text: buf, timestamp: Date.now(), itemId });
                lastAssistantDeltaByItemRef.current[itemId] = buf;
                debugPush('assistant.buffer.flush.on_user_final', { itemId, len: buf.length });
                // Clear buffer after flush
                aiBufferRef.current[itemId] = '';
              }
            }
          } catch {}
          // Replace the placeholder with the final transcript the model consumed
          addMessage({
            role: 'user',
            text: finalText,
            timestamp: Date.now(),
            itemId: msg.item_id,
            isFinal: true,
          });
          // Remove from pending and increment turn counter
          pendingUserRef.current.delete(msg.item_id);
          conversationTurnRef.current++;
          console.log(`🔢 Conversation turn: ${conversationTurnRef.current}`);
          // Track for pairing with next assistant delta
          lastUserFinalRef.current = { itemId: msg.item_id, text: finalText };
          awaitingAssistantForRef.current = msg.item_id;
          return;
        }
        
        // Handle conversation item created events
        if (msg.type === 'conversation.item.created') {
          // Map response_id to the created item if present
          if (msg.response_id && msg.item?.id) {
            responseIdToItemIdRef.current[msg.response_id] = msg.item.id;
          }
        }
      
        // Assistant audio transcript: stream only if no text stream has started for this item
        if (msg.type === 'response.audio_transcript.delta') {
          const frag = msg.delta;
          const mapped = msg.response_id ? responseIdToItemIdRef.current[msg.response_id] : undefined;
          const itemId = msg.item_id || mapped || msg.response_id;
          if (awaitingAssistantForRef.current) {
            console.log('🔗 Pairing user->assistant (audio)', {
              user_item: awaitingAssistantForRef.current,
              user_text: lastUserFinalRef.current?.text?.slice(0, 120) || ''
            });
            awaitingAssistantForRef.current = null;
          }
          if (!hasTextStreamByItemRef.current[itemId]) {
            if (pendingUserRef.current.size > 0) {
              aiBufferRef.current[itemId] = (aiBufferRef.current[itemId] || '') + frag;
            } else {
              if (!assistantMsgInitializedRef.current.has(itemId)) {
                addMessage({ role: 'assistant', text: '', timestamp: Date.now(), itemId });
                assistantMsgInitializedRef.current.add(itemId);
              }
              const last = lastAssistantDeltaByItemRef.current[itemId] || '';
              if (frag && frag !== last) {
                addMessage({ role: 'assistant', text: frag, timestamp: Date.now(), itemId });
                lastAssistantDeltaByItemRef.current[itemId] = frag;
              }
            }
            setIsAISpeaking(true);
          }
          const preview = (frag || '').slice(0, 20);
          const cumLen = (aiBufferRef.current[itemId] || '').length;
          debugPush('response.audio_transcript.delta', { len: (frag || '').length, itemId, preview, cumLen });
        }

        if (msg.type === 'response.audio_transcript.done') {
          const itemId = msg.item_id || msg.response_id;
          if (!hasTextStreamByItemRef.current[itemId]) {
            delete aiBufferRef.current[itemId];
          }
          setIsAISpeaking(false);
          debugPush('response.audio_transcript.done', { itemId });
        }

        // Handle response.output_item events (new format when tools are enabled)
        if (msg.type === 'response.output_item.added') {
          if (msg.item?.type === 'message' && msg.item?.role === 'assistant') {
            // Initialize message tracking for this item
            aiBufferRef.current[msg.item.id] = '';
            lastAssistantDeltaByItemRef.current[msg.item.id] = '';
            assistantFinalizedRef.current.delete(msg.item.id);
            if (msg.response_id) {
              responseIdToItemIdRef.current[msg.response_id] = msg.item.id;
            }
            if (!assistantMsgInitializedRef.current.has(msg.item.id)) {
              addMessage({ role: 'assistant', text: '', timestamp: Date.now(), itemId: msg.item.id });
              assistantMsgInitializedRef.current.add(msg.item.id);
            }
          }
        }

        // Handle content parts for message items
        if (msg.type === 'response.content_part.added') {
          // Content part added to a message
          if (msg.part?.type === 'text') {
            // Track which item this content belongs to
            aiBufferRef.current[msg.item_id] = '';
            lastAssistantDeltaByItemRef.current[msg.item_id] = '';
            if (!assistantMsgInitializedRef.current.has(msg.item_id)) {
              addMessage({ role: 'assistant', text: '', timestamp: Date.now(), itemId: msg.item_id });
              assistantMsgInitializedRef.current.add(msg.item_id);
            }
          }
        }

        if (msg.type === 'response.text.delta') {
          // Text delta for assistant messages when tools are enabled
          const text = msg.delta;
          const mapped = msg.response_id ? responseIdToItemIdRef.current[msg.response_id] : undefined;
          const itemId = msg.item_id || mapped || msg.response_id;
          if (awaitingAssistantForRef.current) {
            console.log('🔗 Pairing user->assistant (text)', {
              user_item: awaitingAssistantForRef.current,
              user_text: lastUserFinalRef.current?.text?.slice(0, 120) || ''
            });
            awaitingAssistantForRef.current = null;
          }
          hasTextStreamByItemRef.current[itemId] = true;
          
          // Check if we should buffer or display immediately
        if (pendingUserRef.current.size > 0) {
            // Buffer the assistant response while user is speaking
            aiBufferRef.current[itemId] = (aiBufferRef.current[itemId] || '') + text;
          } else {
            if (!assistantMsgInitializedRef.current.has(itemId)) {
              addMessage({ role: 'assistant', text: '', timestamp: Date.now(), itemId });
              assistantMsgInitializedRef.current.add(itemId);
            }
          // Sanitize non-speech artifacts (model markers / raw JSON tool acks)
          const sanitized = (text || '')
            .replace(/<\|[^|]+\|>/g, '') // drop tokens like <|has_watermark|>
            .trim();
          const looksLikeJsonAck = /^\{\s*"success"\s*:\s*(true|false)/.test(sanitized);
          const last = lastAssistantDeltaByItemRef.current[itemId] || '';
          if (sanitized && sanitized !== last && !looksLikeJsonAck) {
            addMessage({ role: 'assistant', text: sanitized, timestamp: Date.now(), itemId });
            lastAssistantDeltaByItemRef.current[itemId] = sanitized;
          }
          }
          setIsAISpeaking(true);
          const preview = (text || '').slice(0, 20);
          const cumLen = (aiBufferRef.current[itemId] || '').length;
          debugPush('response.text.delta', { len: (text || '').length, itemId, preview, cumLen });
        }

        if (msg.type === 'response.text.done') {
          // Text completion for a specific item
          const mapped = msg.response_id ? responseIdToItemIdRef.current[msg.response_id] : undefined;
          const itemId = msg.item_id || mapped || msg.response_id;
          if (assistantFinalizedRef.current.has(itemId)) {
            return;
          }
          
          // Text completed
          
          // Just mark the end of speaking, messages are already shown via deltas
          setIsAISpeaking(false);
          // Clean up any buffer references
          delete aiBufferRef.current[itemId];
          assistantFinalizedRef.current.add(itemId);
          debugPush('response.text.done', { itemId });
        }

        if (msg.type === 'response.output_item.done') {
          // Output item completed
          if (msg.item?.type === 'message') {
            setIsAISpeaking(false);
            // Clean up buffer for this item
            delete aiBufferRef.current[msg.item.id];
          }
        }

        // Handle function calls from Tally
        if (msg.type === 'response.function_call_arguments.start') {
          // Function call starting - reset buffer
          functionCallBuffer.current = '';
        }

        if (msg.type === 'response.function_call_arguments.delta') {
          // Silently accumulate function call arguments
          functionCallBuffer.current += msg.delta || '';
        }

        if (msg.type === 'response.function_call_arguments.done') {
          console.log('✅ Function call complete:', { name: msg.name, arguments: functionCallBuffer.current, call_id: msg.call_id });
          // Record call start for duration calculation on function_output
          if (msg.call_id) callStartByIdRef.current[msg.call_id] = Date.now();
          // Primary request event (deduped by call_id)
          try {
            const parsedArgs = (() => { try { return JSON.parse(functionCallBuffer.current || '{}'); } catch { return functionCallBuffer.current; } })();
            const ev = {
              ts: Date.now(),
              type: 'function_call',
              phase: 'request',
              call_id: msg.call_id,
              name: msg.name,
              args: parsedArgs
            };
            seenFunctionCallsRef.current.add(msg.call_id || `${msg.name}-${ev.ts}`);
            debugEventsRef.current.push(ev);
            if (onDebugUpdate) onDebugUpdate({ session: lastSentSessionRef.current, meta: metaRef.current, events: debugEventsRef.current });
          } catch {}
        }
        
        // Also check for function calls in output items (following console pattern)
        if (msg.type === 'response.output_item.done') {
          // Messages do not have a `name` field; log something meaningful across item types.
          console.log('📦 Output item done:', msg.item?.type, msg.item?.name || msg.item?.role || msg.item?.id || '(no name)');
          if (msg.item?.type === 'function_call') {
            console.log(`🎯 Function called: ${msg.item.name}`);
            // Handle function call from output item
            try {
              const args = JSON.parse(msg.item.arguments);
              console.log(`🔧 Processing function: ${msg.item.name}`);
              try {
                // Deduplicate: only log function_call here if we didn't see arguments.done
                const callId = (msg.item as any).call_id;
                const seenKey = callId || `${msg.item.name}-fallback`;
                if (!seenFunctionCallsRef.current.has(seenKey)) {
                  debugEventsRef.current.push({
                    ts: Date.now(),
                    type: 'function_call',
                    phase: 'request',
                    call_id: callId,
                    name: msg.item.name,
                    args
                  });
                  seenFunctionCallsRef.current.add(seenKey);
                  if (onDebugUpdate) onDebugUpdate({ session: lastSentSessionRef.current, meta: metaRef.current, events: debugEventsRef.current });
                }
              } catch {}
              
              if (msg.item.name === 'create_vocab_card' && onWorkspaceRequest) {
                const { word, definition, examples, topic, context } = args;
                try {
                  const resp = await fetch(executeUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
                    body: JSON.stringify({
                      tool: 'create_vocab_card',
                      tool_version: '1.0.0',
                      args: { word, definition, examples, topic, context },
                      context: {
                        userId: (window as any)?.currentUserEmail || 'anonymous',
                        sessionId: (resumeData?.sessionId) || `session_${Date.now()}`,
                        client: 'web',
                        appVersion: process.env.REACT_APP_VERSION || 'dev',
                        model: 'gpt-realtime'
                      }
                    })
                  });
                  const data = await resp.json();
                  if (!resp.ok || !data?.ok) {
                    throw new Error(data?.error?.message || `Tool exec failed (${resp.status})`);
                  }
                  const r = data.result || { word, definition, examples, topic, context };
                  await onWorkspaceRequest('vocabulary', r.topic || 'Vocabulary', r.context || '', r.word, r.definition, r.examples);
                  sendFunctionResponse(
                    msg.item.call_id,
                    { success: true, message: 'Vocabulary added', result: r },
                    'create_vocab_card'
                  );
                } catch (err: any) {
                  console.error('create_vocab_card failed:', err);
                  onError?.(err?.message || 'Failed to add vocabulary');
                  sendFunctionResponse(msg.item.call_id, { success: false, message: err?.message || 'Tool execution failed' }, 'create_vocab_card');
                }
              } else if (msg.item.name === 'create_concept_card' && onWorkspaceRequest) {
                try {
                  const { topic = 'Concept Overview', description, specifics } = args || {};
                  // Request workspace card (UI) and capture generated markdown via return payload when available
                  const wr = await onWorkspaceRequest('summary', topic, '');
                  let resultForModel: any = undefined;
                  if (wr && (wr as any).content) {
                    resultForModel = { topic, markdown: (wr as any).content, description, specifics };
                  }
                  sendFunctionResponse(
                    msg.item.call_id,
                    { success: true, message: 'Concept overview created', result: resultForModel },
                    'create_concept_card'
                  );
                } catch (err: any) {
                  console.error('create_concept_card failed:', err);
                  onError?.(err?.message || 'Failed to add concept overview');
                  sendFunctionResponse(msg.item.call_id, { success: false, message: err?.message || 'Tool execution failed' }, 'create_concept_card');
                }
              } else if (msg.item.name === 'create_exercise_card' && onWorkspaceRequest) {
                try {
                  const exerciseSeed = {
                    topic: args.topic || args.title || undefined,
                    target_phrase: args.target_phrase || args.description || undefined,
                    level: args.level || undefined,
                    abilities: Array.isArray(args.abilities) ? args.abilities : undefined,
                    skills: Array.isArray(args.skills) ? args.skills : undefined
                  };
                  const wr = await onWorkspaceRequest('exercise', 'Practice Exercises', 'Generated from conversation', undefined, undefined, undefined, exerciseSeed);
                  let resultForModel: any = undefined;
                  try {
                    if (wr && (wr as any).content) {
                      const parsed = JSON.parse((wr as any).content);
                      // Expecting shape: { questions?: [...], topic?: string, ... }
                      if (parsed && (parsed.questions || parsed.exercise || parsed.data)) {
                        resultForModel = {
                          topic: parsed.topic || exerciseSeed.topic,
                          questions: parsed.questions || parsed.data || [],
                          policy: {
                            mcq: {
                              disclose_correct_answer_only_on_explicit_request: true,
                              provide_hints_if_user_struggles: true
                            }
                          }
                        };
                      } else {
                        resultForModel = {
                          topic: exerciseSeed.topic,
                          policy: {
                            mcq: {
                              disclose_correct_answer_only_on_explicit_request: true,
                              provide_hints_if_user_struggles: true
                            }
                          }
                        };
                      }
                    } else {
                      resultForModel = {
                        topic: exerciseSeed.topic,
                        policy: {
                          mcq: {
                            disclose_correct_answer_only_on_explicit_request: true,
                            provide_hints_if_user_struggles: true
                          }
                        }
                      };
                    }
                  } catch {}
                  sendFunctionResponse(
                    msg.item.call_id,
                    { success: true, message: 'Exercises added', result: resultForModel },
                    'create_exercise_card'
                  );
                } catch (err: any) {
                  console.error('create_exercise_card failed:', err);
                  onError?.(err?.message || 'Failed to add exercises');
                  sendFunctionResponse(msg.item.call_id, { success: false, message: err?.message || 'Tool execution failed' }, 'create_exercise_card');
                }
              } else if (msg.item.name === 'generate_skill_assessment' && onWorkspaceRequest) {
                console.log('📊 Generating skill assessment...');
                const { motivational_statement, category_scores, skills_feedback } = args;
                
                console.log('📊 Assessment data received:', {
                  motivational_statement,
                  category_scores,
                  skills_feedback
                });
                
                try {
                  console.log('POST generate_skill_assessment ->', executeUrl);
                  const resp = await fetch(executeUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
                    body: JSON.stringify({
                      tool: 'generate_skill_assessment',
                      tool_version: '1.0.0',
                      args: { motivational_statement, category_scores, skills_feedback },
                      context: {
                        client: 'web',
                        appVersion: process.env.REACT_APP_VERSION || 'dev'
                      }
                    })
                  });
                  const raw = await resp.text();
                  console.log('generate_skill_assessment resp.url', resp.url, 'status', resp.status);
                  let data: any = null;
                  try { data = raw ? JSON.parse(raw) : null; } catch {}
                  if (!resp.ok || !data?.ok) {
                    console.error('generate_skill_assessment exec failed', { status: resp.status, raw });
                    const message = data?.error?.message || raw || `Tool exec failed (${resp.status})`;
                    throw new Error(message);
                  }
                  const normalized = data.result || { motivational_statement, category_scores, skills_feedback };
                  await onWorkspaceRequest(
                    'skill_assessment',
                    'Session Skills Report',
                    'End of session assessment',
                    undefined,
                    undefined,
                    undefined,
                    {
                      motivational_statement: normalized.motivational_statement,
                      category_scores: normalized.category_scores,
                      skills_feedback: normalized.skills_feedback
                    }
                  );
                  sendFunctionResponse(
                    msg.item.call_id,
                    { success: true, message: 'Skill assessment added to workspace', result: normalized },
                    'generate_skill_assessment'
                  );
                } catch (err: any) {
                  console.error('Skill assessment failed:', err);
                  onError?.(err?.message || 'Failed to generate assessment');
                  sendFunctionResponse(
                    msg.item.call_id,
                    { success: false, message: 'Failed to generate assessment' },
                    'generate_skill_assessment'
                  );
                }
              } else if (msg.item.name === 'generate_session_metadata') {
                console.log('📝 Generating session metadata...');
                const { title, description, topic_category } = args;
                
                // Check if we should actually generate metadata yet
                if (conversationTurnRef.current < 2) {
                  console.log(`⏳ Too early for metadata (turn ${conversationTurnRef.current}), waiting for actual topic...`);
                  // Send response but don't store metadata
                  sendFunctionResponse(
                    msg.item.call_id,
                    { success: true, message: 'Waiting for conversation topic' },
                    'generate_session_metadata'
                  );
                } else if (!metadataGeneratedRef.current) {
                  try {
                    console.log('POST generate_session_metadata ->', executeUrl);
                    const resp = await fetch(executeUrl, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
                      body: JSON.stringify({
                        tool: 'generate_session_metadata',
                        tool_version: '1.0.1',
                        args: { title, description, topic_category },
                        context: {
                          userId: (window as any)?.currentUserEmail || 'anonymous',
                          sessionId: (resumeData?.sessionId) || `session_${Date.now()}`,
                          client: 'web',
                          appVersion: process.env.REACT_APP_VERSION || 'dev',
                          model: 'gpt-realtime'
                        }
                      })
                    });
                    const raw = await resp.text();
                    console.log('generate_session_metadata resp.url', resp.url, 'status', resp.status);
                    let data: any = null;
                    try { data = raw ? JSON.parse(raw) : null; } catch {}
                    if (!resp.ok || !data?.ok) {
                      console.error('generate_session_metadata exec failed', { status: resp.status, raw });
                      const message = data?.error?.message || raw || `Tool exec failed (${resp.status})`;
                      throw new Error(message);
                    }
                    const result = data.result || {};
                    const normTitle = result.title ?? title;
                    const normDesc = result.description ?? description;
                    const normCat = result.topic_category ?? topic_category;
                    
                    // Store metadata for later use when saving conversation
                    metadataGeneratedRef.current = true;
                    window.sessionMetadata = { title: normTitle, description: normDesc, topic_category: normCat };
                    console.log('✅ Session metadata stored (server):', window.sessionMetadata);
                    
                    // Update UI
                    if (onSessionMetadata) {
                      onSessionMetadata(normTitle, normDesc, normCat);
                    }
                    
                  // Send success response with result
                    sendFunctionResponse(
                      msg.item.call_id,
                    { success: true, message: 'Session metadata generated', result: { title: normTitle, description: normDesc, topic_category: normCat } },
                      'generate_session_metadata'
                    );
                  } catch (err: any) {
                    console.error('Metadata tool execution error:', err);
                    onError?.(err?.message || 'Failed to generate session metadata');
                    sendFunctionResponse(
                      msg.item.call_id,
                      { success: false, message: err?.message || 'Tool execution failed' },
                      'generate_session_metadata'
                    );
                  }
                } else {
                  // Metadata already generated, skip
                  console.log('⚠️ Metadata already generated, skipping');
                  sendFunctionResponse(
                    msg.item.call_id,
                    { success: true, message: 'Metadata already set' },
                    'generate_session_metadata'
                  );
                }
              } else if (msg.item.name === 'generate_simulation_scorecard' && onWorkspaceRequest) {
                // Forward to backend tool executor and render simulation scorecard
                try {
                  const scoreArgs = args || {};
                  const resp = await fetch(executeUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
                    body: JSON.stringify({
                      tool: 'generate_simulation_scorecard',
                      tool_version: '1.0.0',
                      args: scoreArgs,
                      context: {
                        client: 'web',
                        appVersion: process.env.REACT_APP_VERSION || 'dev',
                        model: 'gpt-realtime'
                      }
                    })
                  });
                  const raw = await resp.text();
                  let data: any = null;
                  try { data = raw ? JSON.parse(raw) : null; } catch {}
                  if (!resp.ok || !data?.ok) {
                    throw new Error(data?.error?.message || `Tool exec failed (${resp.status})`);
                  }
                  const scorecard = data.result;
                  await onWorkspaceRequest('simulation_scorecard', 'Simulation Scorecard', '', undefined, undefined, undefined, scorecard);
                  sendFunctionResponse(msg.item.call_id, { success: true, message: 'Scorecard generated' }, 'generate_simulation_scorecard');
                } catch (err: any) {
                  console.error('generate_simulation_scorecard failed:', err);
                  onError?.(err?.message || 'Failed to generate simulation scorecard');
                  sendFunctionResponse(msg.item.call_id, { success: false, message: err?.message || 'Tool execution failed' }, 'generate_simulation_scorecard');
                }
              }
              else if (msg.item.name === 'generate_tally_learning_path' && onWorkspaceRequest) {
                try {
                  // Guard: prevent repeated rapid retries (can happen if model keeps calling after an error)
                  const now = Date.now();
                  if (now < (learningPathCooldownUntilRef.current || 0)) {
                    sendFunctionResponse(
                      msg.item.call_id,
                      { success: false, message: 'Learning path generation is cooling down after a recent error. Do not retry yet; ask the student one more question and try later.' },
                      'generate_tally_learning_path'
                    );
                    return;
                  }
                  // Pass any provided constraints/model along; transcript is generated client-side from messages
                  const wr = await onWorkspaceRequest('learning_path', 'Learning Path', '', undefined, undefined, undefined, args);
                  let resultForModel: any = undefined;
                  if (wr && (wr as any).content) {
                    try {
                      resultForModel = JSON.parse((wr as any).content);
                    } catch (e) {
                      throw new Error('Learning path tool returned non-JSON content');
                    }
                  }
                  sendFunctionResponse(
                    msg.item.call_id,
                    { success: true, message: 'Learning path generated', result: resultForModel },
                    'generate_tally_learning_path'
                  );
                } catch (err: any) {
                  console.error('generate_tally_learning_path failed:', err);
                  onError?.(err?.message || 'Failed to generate learning path');
                  learningPathAttemptedRef.current = true;
                  // Avoid a tight retry loop, but do not block for 60s (too long for iterative UX).
                  // This is a small debounce only.
                  learningPathCooldownUntilRef.current = Date.now() + 2_000;
                  sendFunctionResponse(
                    msg.item.call_id,
                    { success: false, message: (err?.message || 'Tool execution failed') + ' (Do not retry automatically; ask one more intake question, then try again later.)' },
                    'generate_tally_learning_path'
                  );
                }
              }
            } catch (err) {
              console.error('Function parsing error:', err);
            }
          }
        }
      };
      

      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);

      const answerResp = await fetch(
        `${OPENAI_REALTIME_URL}?model=${(overrideModelRef?.current || OPENAI_REALTIME_MODEL)}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/sdp' },
          body: offer.sdp!
        }
      );
      if (!answerResp.ok) throw new Error(`Handshake failed: ${answerResp.status}`);
      const answerSdp = await answerResp.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
    } catch (err: any) {
      onError(err.message);
      cleanup();
    } finally {
      setConnecting(false);
    }
  };

  const pauseConversation = async () => {
    if (!dcRef.current || dcRef.current.readyState !== 'open' || isPaused) return;
    
    console.log('⏸️ Pausing conversation...');
    
    try {
      // Politely stop any active assistant speech/text (only if speaking)
      if (isAISpeaking) {
        try {
          dcRef.current.send(JSON.stringify({ type: 'response.cancel' }));
        } catch {}
      }
      
      // a) Disable automatic turns and audio output
      console.log('Disabling turn detection and switching to text-only...');
      dcRef.current.send(JSON.stringify({
        type: 'session.update',
        session: {
          turn_detection: null,  // Disable VAD during pause
          modalities: ['text']   // Text-only during pause (no TTS)
        }
      }));
      // b.1) Immediately mute remote playback locally so user hears stop instantly
      try {
        if (remoteStreamRef.current && !remoteMutedRef.current) {
          remoteStreamRef.current.getAudioTracks().forEach(t => (t.enabled = false));
          remoteMutedRef.current = true;
        }
      } catch {}
      
      // b) Drop any mic audio buffered on the server
      console.log('Clearing audio buffer...');
      dcRef.current.send(JSON.stringify({
        type: 'input_audio_buffer.clear'
      }));
      
      // c) Stop sending mic audio (no RTP → no audio-minute billing)
      console.log('Detaching microphone track...');
      if (micSenderRef.current) {
        await micSenderRef.current.replaceTrack(null);
      }
      
      // d) Mute any residual playback locally
      // Note: We don't have direct access to audio element in this hook
      // This would need to be handled in the component if needed
      
      setIsPaused(true);
      console.log('✅ Conversation paused successfully');
      
      // Start auto-disconnect timer (2 minutes + 30 second warning)
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
      pauseTimeoutRef.current = setTimeout(() => {
        // Start 30-second countdown
        setDisconnectCountdown(30);
        
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
        const countdownInterval = setInterval(() => {
          setDisconnectCountdown(prev => {
            if (prev === null || prev <= 1) {
              clearInterval(countdownInterval);
              countdownIntervalRef.current = null;
              console.log('🔌 Auto-disconnecting after pause timeout...');
              cleanup();
              return null;
            }
            return prev - 1;
          });
        }, 1000);
        
        // Store interval reference to clear if resumed
        countdownIntervalRef.current = countdownInterval as any;
      }, 120000); // 2 minutes
      
    } catch (error) {
      console.error('Error pausing conversation:', error);
      onError('Failed to pause conversation');
    }
  };

  const resumeConversation = async () => {
    if (!dcRef.current || dcRef.current.readyState !== 'open' || !isPaused) return;
    
    console.log('▶️ Resuming conversation...');
    
    // Clear any auto-disconnect timer
    if (pauseTimeoutRef.current) { clearTimeout(pauseTimeoutRef.current); pauseTimeoutRef.current = null; }
    if (countdownIntervalRef.current) { clearInterval(countdownIntervalRef.current); countdownIntervalRef.current = null; }
    setDisconnectCountdown(null);
    
    try {
      // a) Reattach microphone
      console.log('Reattaching microphone track...');
      if (micSenderRef.current && micTrackRef.current) {
        await micSenderRef.current.replaceTrack(micTrackRef.current);
      }
      
      // b) Re-enable VAD and TTS
      console.log('Re-enabling turn detection and audio modalities...');
      dcRef.current.send(JSON.stringify({
        type: 'session.update',
        session: {
          turn_detection: { 
            type: 'server_vad',
            threshold: 0.5,
            prefix_padding_ms: 300,
            silence_duration_ms: 500
          },
          modalities: ['text', 'audio']  // Re-enable both text and audio
        }
      }));
      // b.1) Unmute remote playback
      try {
        if (remoteStreamRef.current && remoteMutedRef.current) {
          remoteStreamRef.current.getAudioTracks().forEach(t => (t.enabled = true));
          remoteMutedRef.current = false;
        }
      } catch {}
      
      // Note: Audio element unmuting would be handled in component if needed
      
      setIsPaused(false);
      console.log('✅ Conversation resumed successfully');
    } catch (error) {
      console.error('Error resuming conversation:', error);
      onError('Failed to resume conversation');
    }
  };
  
  // Helper function to send function response and trigger continuation
  const sendFunctionResponse = (callId: string, output: any, functionName: string) => {
    if (!dcRef.current || dcRef.current.readyState !== 'open') return;
    
    // Send the function response
    const response = {
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: JSON.stringify(output)
      }
    };
    dcRef.current.send(JSON.stringify(response));
    console.log(`✅ Sent ${functionName} response`);
    try {
      const now = Date.now();
      const started = callStartByIdRef.current[callId];
      const durationMs = typeof started === 'number' ? Math.max(0, now - started) : undefined;
      debugEventsRef.current.push({
        ts: Date.now(),
        type: 'function_output',
        call_id: callId,
        phase: 'response',
        durationMs,
        name: functionName,
        output
      });
      if (onDebugUpdate) onDebugUpdate({ session: lastSentSessionRef.current, meta: metaRef.current, events: debugEventsRef.current });
    } catch {}
    
    // Trigger model continuation only when the tool succeeded.
    // If we trigger after an error output, the realtime API may already have an active response,
    // causing `conversation_already_has_active_response` and thrashing.
    const shouldContinue = !(output && (output.success === false));
    if (shouldContinue) {
      setTimeout(() => {
        if (dcRef.current && dcRef.current.readyState === 'open') {
          dcRef.current.send(JSON.stringify({ type: 'response.create' }));
          console.log(`🔄 Triggered response continuation after ${functionName}`);
        }
      }, 100);
    } else {
      console.log(`⛔️ Skipping response continuation after ${functionName} (success=false)`);
    }
  };

  const sendFarewell = () => {
    if (!dcRef.current || dcRef.current.readyState !== 'open') return;
    
    console.log('👋 Sending farewell message to trigger skill assessment');
    
    // Send a user message that will trigger Tally's skill assessment
    const farewellMessage = {
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [{
          type: 'input_text',
          text: "That's all for today, I have to go now. Thanks for the practice session!"
        }]
      }
    };
    
    dcRef.current.send(JSON.stringify(farewellMessage));
  };

  // Send a synthetic user text message into the realtime conversation and prompt a reply
  const sendUserMessage = (text: string) => {
    try {
      if (!dcRef.current || dcRef.current.readyState !== 'open') return;
      const msg = {
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [{ type: 'input_text', text }]
        }
      };
      dcRef.current.send(JSON.stringify(msg));
      // Optionally trigger assistant response
      dcRef.current.send(JSON.stringify({ type: 'response.create' }));
      // Also mirror into local transcript for UI continuity if desired
      addMessage({ role: 'user', text, timestamp: Date.now(), itemId: `ui-${Date.now()}` });
      debugPush('user.message.injected', { text });
    } catch (e) {
      console.error('sendUserMessage failed', e);
    }
  };

  const cleanup = () => {
    // Clear any timers
    if (pauseTimeoutRef.current) { clearTimeout(pauseTimeoutRef.current); pauseTimeoutRef.current = null; }
    if (countdownIntervalRef.current) { clearInterval(countdownIntervalRef.current); countdownIntervalRef.current = null; }
    
    dcRef.current?.close();
    pcRef.current?.close();
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    micSenderRef.current = null;
    micTrackRef.current = null;
    setConnected(false);
    setIsSpeaking(false);
    setIsAISpeaking(false);
    setIsPaused(false);
    setDisconnectCountdown(null);
  };

  useEffect(() => {
    // Enable/disable debug from localStorage
    try {
      const v = (localStorage.getItem('tally_debug') || '').toLowerCase();
      debugEnabledRef.current = v === 'true';
    } catch {}
    // Install dump helpers on window
    try {
      const w: any = window as any;
      w.dumpTallyDebug = () => {
        try { return JSON.stringify((w._tallyDebug?.logs || []), null, 2); } catch { return '[]'; }
      };
      w.clearTallyDebug = () => { if (w._tallyDebug) w._tallyDebug.logs = []; };
    } catch {}
    return cleanup;
  }, []);

  return {
    pcRef, dcRef, connected, connecting, isSpeaking, isAISpeaking, isPaused,
    startConversation, pauseConversation, resumeConversation, cleanup, remoteStreamRef,
    disconnectCountdown, sendFarewell,
    // Allow callers (Prompt Studio or URL-parameterized flows) to inject session headers once
    setExtraSessionHeaders: (headers: Record<string, string> | null) => { extraHeadersRef.current = headers; },
    // Allow callers to override realtime model for the handshake
    setRealtimeModel: (model: string | null) => { (overrideModelRef as any).current = model; },
    // Start a simulation with optional session overrides (instructions/voice)
    startSimulation: (opts: { instructions: string; voice?: string; topic?: string }) => {
      simulationModeRef.current = true;
      sessionOverridesRef.current = { instructions: opts.instructions, voice: opts.voice };
      return startConversation(opts.topic);
    },
    // Exit simulation mode explicitly if needed
    endSimulationMode: () => { simulationModeRef.current = false; },
    sendUserMessage,
    // Debug snapshot accessor
    getDebugInfo: () => ({ session: lastSentSessionRef.current, meta: metaRef.current, events: debugEventsRef.current })
  };
};
